import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Tooltip } from 'components/ui';
import {
  HiOutlinePencil,
  HiTrash,
  HiEye,
  HiCheck,
  HiX,
  HiPlay,
  HiDownload,
  HiDuplicate,
  HiCog
} from 'react-icons/hi';

const ACTION_CONFIG = {
  edit: {
    icon: HiOutlinePencil,
    label: 'Edit',
    color: 'text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300',
    tooltip: 'Edit'
  },
  delete: {
    icon: HiTrash,
    label: 'Delete',
    color: 'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300',
    tooltip: 'Delete'
  },
  view: {
    icon: HiEye,
    label: 'View',
    color: 'text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300',
    tooltip: 'View'
  },
  approve: {
    icon: HiCheck,
    label: 'Approve',
    color: 'text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300',
    tooltip: 'Approve'
  },
  reject: {
    icon: HiX,
    label: 'Reject',
    color: 'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300',
    tooltip: 'Reject'
  },
  play: {
    icon: HiPlay,
    label: 'Play',
    color: 'text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300',
    tooltip: 'Play'
  },
  download: {
    icon: HiDownload,
    label: 'Download',
    color: 'text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300',
    tooltip: 'Download'
  },
  duplicate: {
    icon: HiDuplicate,
    label: 'Duplicate',
    color: 'text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300',
    tooltip: 'Duplicate'
  },
  settings: {
    icon: HiCog,
    label: 'Settings',
    color: 'text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300',
    tooltip: 'Settings'
  }
};

const ActionButton = ({
  action,
  onClick,
  tooltip,
  label,
  icon: CustomIcon,
  disabled,
  className,
  size = 'sm',
  variant = 'link',
  showLabel = false,
  ...rest
}) => {
  const config = ACTION_CONFIG[action] || ACTION_CONFIG.view;
  const Icon = CustomIcon || config.icon;
  const buttonLabel = label || config.label;
  const tooltipText = tooltip || config.tooltip;

  const buttonClasses = classNames(
    'transition-all duration-200',
    config.color,
    'hover:scale-110 active:scale-95',
    disabled && 'opacity-50 cursor-not-allowed',
    className
  );

  const button = (
    <Button
      size={size}
      variant={variant}
      onClick={onClick}
      disabled={disabled}
      icon={<Icon />}
      className={buttonClasses}
      {...rest}
    >
      {showLabel && buttonLabel}
    </Button>
  );

  // Only wrap with Tooltip if tooltip is enabled and button is not disabled
  if (tooltipText && !disabled && !showLabel) {
    return (
      <Tooltip title={tooltipText} placement="top">
        {button}
      </Tooltip>
    );
  }

  return button;
};

ActionButton.propTypes = {
  action: PropTypes.oneOf([
    'edit',
    'delete',
    'view',
    'approve',
    'reject',
    'play',
    'download',
    'duplicate',
    'settings'
  ]).isRequired,
  onClick: PropTypes.func.isRequired,
  tooltip: PropTypes.string,
  label: PropTypes.string,
  icon: PropTypes.elementType,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  variant: PropTypes.oneOf(['link', 'plain', 'solid', 'outline']),
  showLabel: PropTypes.bool
};

const ActionButtonGroup = ({ children, className, spacing = 'gap-2' }) => {
  const groupClasses = classNames(
    'flex items-center',
    spacing,
    className
  );

  return <div className={groupClasses}>{children}</div>;
};

ActionButtonGroup.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  spacing: PropTypes.string
};

export { ActionButton };
export default ActionButtonGroup;
